import { useState } from "react";
import { CalendarClock, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { CyclesListSkeleton } from "./CyclesListSkeleton";
import { CyclesTable } from "./CyclesTable";
import { GenerateDraftDialog } from "./GenerateDraftDialog";
import { GenerateDraftBanner } from "./GenerateDraftBanner";
import { ScheduleRolloverDialog } from "./ScheduleRolloverDialog";
import { PendingRolloverSchedules } from "./PendingRolloverSchedules";
import { useCommercialCycles } from "./useCommercialCycles";
import { useGenerateDraftNotifications } from "./useGenerateDraftNotifications";
import { useRolloverSchedules } from "./useRolloverSchedules";
import {
  useActiveGenerateDraftRun,
  useGenerateDraftRun,
  useGenerateDraftRunProgress,
} from "./generateDraftRun";

export default function CyclesList() {
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // ─── Dados e mutations ───────────────────────────────────────────────────────
  const {
    cycles,
    isLoading,
    invalidate,
    publishMutation,
    openMutation,
    startClosingMutation,
    closeMutation,
    handlePublish,
    handleOpen,
    handleStartClosing,
    handleClose,
    ConfirmDialog,
  } = useCommercialCycles();

  // ─── Generate draft run (polling + progresso) ────────────────────────────────
  const [activeRun, setActiveRun] = useActiveGenerateDraftRun();
  const run = useGenerateDraftRun(activeRun?.runId ?? null);

  const runIsRunning = !!activeRun && (!run || run.status === "Running");
  const progress = useGenerateDraftRunProgress(activeRun?.runId ?? null, runIsRunning);
  const isGenerating = runIsRunning && (!progress || progress.status === "Running");

  // ─── Side effects de notificação ─────────────────────────────────────────────
  useGenerateDraftNotifications(activeRun, run, progress, invalidate);

  // ─── Agendamento de tombamento ────────────────────────────────────────────────
  const {
    schedules: rolloverSchedules,
    isLoading: isLoadingRolloverSchedules,
    invalidate: invalidateRolloverSchedules,
    handleCancel: handleCancelRolloverSchedule,
    cancelMutation: cancelRolloverScheduleMutation,
    ConfirmDialog: CancelRolloverScheduleConfirmDialog,
  } = useRolloverSchedules();

  // ─── Rendering ───────────────────────────────────────────────────────────────
  if (isLoading) return <CyclesListSkeleton />;

  return (
    <Layout title="Ciclos Comerciais">
      <div className="space-y-6">
        <ConfirmDialog />
        <CancelRolloverScheduleConfirmDialog />
        <GenerateDraftDialog
          open={generateDialogOpen}
          onOpenChange={setGenerateDialogOpen}
          cycles={cycles}
          onGenerated={invalidate}
          activeRun={activeRun}
          run={run}
          isGenerating={isGenerating}
          progress={progress}
          onRunStarted={setActiveRun}
          onRunCleared={() => setActiveRun(null)}
        />
        <ScheduleRolloverDialog
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          cycles={cycles}
          pendingSchedules={rolloverSchedules}
          onScheduled={invalidateRolloverSchedules}
        />

        {/* Cabeçalho da página — descrição + ações */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarClock className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Gestão dos ciclos comerciais coexistentes (ex.: 2026, 2027).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setScheduleDialogOpen(true)}>
              <CalendarClock className="h-4 w-4 mr-2" /> Agendar tombamento
            </Button>
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Gerar rascunho
            </Button>
          </div>
        </div>

        {activeRun && (
          <GenerateDraftBanner
            activeRun={activeRun}
            run={run}
            isGenerating={isGenerating}
            progress={progress}
            onDismiss={() => setActiveRun(null)}
          />
        )}

        <Card className="border-border shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Ciclos</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <CyclesTable
              cycles={cycles}
              onPublish={handlePublish}
              onOpen={handleOpen}
              onStartClosing={handleStartClosing}
              onClose={handleClose}
              isPublishing={publishMutation.isPending}
              isOpening={openMutation.isPending}
              isStartingClosing={startClosingMutation.isPending}
              isClosing={closeMutation.isPending}
            />
          </CardContent>
        </Card>

        <PendingRolloverSchedules
          schedules={rolloverSchedules}
          isLoading={isLoadingRolloverSchedules}
          onCancel={handleCancelRolloverSchedule}
          isCancelling={cancelRolloverScheduleMutation.isPending}
        />
      </div>
    </Layout>
  );
}
